import { useState, useRef } from "react";
import { Plus, Pencil, Trash2, AlertTriangle, ShieldAlert, Upload, Download, GripVertical, History, Repeat, ShoppingCart, Anchor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import DebtDialog from "@/components/DebtDialog";
import DebtPaymentHistoryDialog from "@/components/DebtPaymentHistoryDialog";
import DebtPurchaseDialog from "@/components/DebtPurchaseDialog";
import SetCurrentBalanceDialog from "@/components/SetCurrentBalanceDialog";
import BalanceTransferDialog from "@/components/BalanceTransferDialog";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const fmt = (v) => `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Returns a tailwind colour pair based on credit utilisation %.
// <30% green · 30-70% amber · 70-90% red · >=90% deep red.
function utilColours(pct) {
  if (pct < 30) return { bar: "bg-green-500",  text: "text-green-700",  bg: "bg-green-50",  border: "border-green-300" };
  if (pct < 70) return { bar: "bg-amber-500",  text: "text-amber-700",  bg: "bg-amber-50",  border: "border-amber-300" };
  if (pct < 90) return { bar: "bg-red-500",    text: "text-red-700",    bg: "bg-red-50",    border: "border-red-300" };
  return                { bar: "bg-red-700",    text: "text-red-800",    bg: "bg-red-100",   border: "border-red-500" };
}

function UtilizationCell({ debt }) {
  const limit = Number(debt.credit_limit || 0);
  if (limit <= 0) return <span className="text-gray-300 text-xs">—</span>;
  const pct = Math.min(999, (Number(debt.total_balance || 0) / limit) * 100);
  const c = utilColours(pct);
  return (
    <div className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm border ${c.border} ${c.bg}`} data-testid={`debt-util-${debt.id}`}>
      <div className="w-10 h-1.5 bg-white/70 rounded-sm overflow-hidden">
        <div className={`h-full ${c.bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className={`font-mono-data text-[10px] font-semibold ${c.text}`}>{pct.toFixed(0)}%</span>
    </div>
  );
}

function SortableDebtRow({ d, i, handleEdit, setDeleteTarget, onShowHistory, onAddPurchase, onSetCurrentBalance }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: d.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <tr ref={setNodeRef} style={style} key={d.id} data-testid={`debt-row-${d.id}`}>
      <td className="text-center w-8">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-100 rounded-sm" data-testid={`drag-debt-${d.id}`}>
          <GripVertical className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
        </button>
      </td>
      <td className="font-medium text-gray-900 whitespace-nowrap">
        {d.name}
        {d.is_deferred_interest && (
          <Badge className="ml-1.5 border border-amber-400 text-amber-700 bg-amber-50 text-[10px] px-1 py-0 rounded-sm font-mono-data" variant="outline">DEFERRED</Badge>
        )}
      </td>
      <td className="text-center font-mono-data mobile-hide">{d.day_due}</td>
      <td className="numeric mobile-hide">{fmt(d.balance)}</td>
      <td className="numeric mobile-hide">{d.promo_balance > 0 ? fmt(d.promo_balance) : "—"}</td>
      <td className="numeric font-semibold">{fmt(d.total_balance)}</td>
      <td className="text-center mobile-hide"><UtilizationCell debt={d} /></td>
      <td className="text-center font-mono-data text-xs">
        {d.promo_until && d.promo_status !== "expired" ? (
          <span>0% <span className="text-gray-400">→</span> <span className="text-gray-600">{d.apr_after}</span></span>
        ) : d.apr_after && d.apr_after !== "0%" && d.apr_after !== "0.00%" ? (
          <span>{d.apr_after}</span>
        ) : (
          <span>{d.apr_now}</span>
        )}
      </td>
      <td className="text-center mobile-hide">
        {d.promo_until ? (
          <span className="inline-flex items-center gap-1">
            {d.promo_status === "expired" ? (
              <Badge className="border border-red-300 text-red-600 bg-red-50 text-xs px-2 py-0.5 rounded-sm font-mono-data" variant="outline">
                <ShieldAlert className="w-3 h-3 mr-1 inline" strokeWidth={1.5} />EXPIRED
              </Badge>
            ) : d.promo_status === "expiring_soon" ? (
              <Badge className="border border-amber-300 text-amber-600 bg-amber-50 text-xs px-2 py-0.5 rounded-sm font-mono-data" variant="outline">
                <AlertTriangle className="w-3 h-3 mr-1 inline" strokeWidth={1.5} />{d.promo_until}
              </Badge>
            ) : (
              <span className="font-mono-data text-xs text-gray-600">{d.promo_until}</span>
            )}
          </span>
        ) : <span className="text-gray-300">—</span>}
      </td>
      <td className="text-center font-mono-data text-xs hidden">{d.apr_after || "—"}</td>
      <td className="numeric">
        {fmt(d.statement_min)}
        {d.min_payment_type === "percentage" && (
          <span className="ml-1 inline-block text-[9px] px-1 py-0 rounded-sm border border-gray-300 bg-gray-100 text-gray-500 font-mono-data align-middle">{d.min_payment_pct}%</span>
        )}
        {d.min_payment_type === "split" && (
          <span className="ml-1 inline-block text-[9px] px-1 py-0 rounded-sm border border-blue-300 bg-blue-50 text-blue-600 font-mono-data align-middle">Split</span>
        )}
      </td>
      <td className="numeric mobile-hide">
        <div>{fmt(d.current_balance)}</div>
        {d.last_payment_date && d.last_payment_amount > 0 && (
          <div
            data-testid={`last-payment-${d.id}`}
            title={`Last payment: ${d.last_payment_date} for ${fmt(d.last_payment_amount)}`}
            className="text-[10px] text-emerald-600 mt-0.5 font-mono-data"
          >
            −{fmt(d.last_payment_amount)} on {(d.last_payment_date || "").slice(5)}
          </div>
        )}
      </td>
      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <button data-testid={`history-debt-${d.id}`} onClick={() => onShowHistory(d)} title="Payment history" className="p-1 hover:bg-gray-100 rounded-sm transition-colors">
            <History className="w-3.5 h-3.5 text-gray-500 action-icon" strokeWidth={1.5} />
          </button>
          <button data-testid={`set-cb-debt-${d.id}`} onClick={() => onSetCurrentBalance(d)} title="Set CURRENT BAL (anchor)" className="p-1 hover:bg-blue-50 rounded-sm transition-colors">
            <Anchor className="w-3.5 h-3.5 text-blue-500 action-icon" strokeWidth={1.5} />
          </button>
          <button data-testid={`add-purchase-${d.id}`} onClick={() => onAddPurchase(d)} title="Record new purchase / spending" className="p-1 hover:bg-red-50 rounded-sm transition-colors">
            <ShoppingCart className="w-3.5 h-3.5 text-red-500 action-icon" strokeWidth={1.5} />
          </button>
          <button data-testid={`edit-debt-${d.id}`} onClick={() => handleEdit(d)} className="p-1 hover:bg-gray-100 rounded-sm transition-colors">
            <Pencil className="w-3.5 h-3.5 text-gray-500 action-icon" strokeWidth={1.5} />
          </button>
          <button data-testid={`delete-debt-${d.id}`} onClick={() => setDeleteTarget(d)} className="p-1 hover:bg-red-50 rounded-sm transition-colors">
            <Trash2 className="w-3.5 h-3.5 text-red-500 action-icon" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function DebtTrackerPage({ debts, onRefresh, api }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDebt, setEditDebt] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyDebt, setHistoryDebt] = useState(null);
  const [purchaseDebt, setPurchaseDebt] = useState(null);
  const [setCBDebt, setSetCBDebt] = useState(null);
  const [btOpen, setBtOpen] = useState(false);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleAdd = () => { setEditDebt(null); setDialogOpen(true); };
  const handleEdit = (debt) => { setEditDebt(debt); setDialogOpen(true); };

  const handleSyncFromBudget = async () => {
    try {
      const res = await axios.post(`${api}/budget/sync-debt-balances`);
      const results = res.data?.results || [];
      const total = results.reduce((s, r) => s + (r.payments_applied || 0), 0);
      toast.success(`Synced ${results.length} debts · ${total} payments applied`);
      onRefresh();
    } catch (e) {
      toast.error("Sync failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSave = async (data) => {
    try {
      if (editDebt) {
        await axios.put(`${api}/debts/${editDebt.id}`, data);
        toast.success("Debt updated");
      } else {
        await axios.post(`${api}/debts`, data);
        toast.success("Debt added");
      }
      setDialogOpen(false);
      onRefresh();
    } catch (e) { toast.error("Failed to save debt"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${api}/debts/${id}`);
      toast.success("Debt deleted");
      setDeleteTarget(null);
      onRefresh();
    } catch (e) { toast.error("Failed to delete debt"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = debts.findIndex(d => d.id === active.id);
    const newIndex = debts.findIndex(d => d.id === over.id);
    const newOrder = arrayMove(debts, oldIndex, newIndex).map(d => d.id);
    try {
      await axios.put(`${api}/debts/reorder`, { order: newOrder });
      await onRefresh();
    } catch { toast.error("Failed to reorder"); }
  };

  const exportDebtsCSV = () => {
    const header = "Name,Day Due,Balance,Promo Balance,APR Now,0% Until,APR After,Statement Min,Current Balance,Deferred Interest";
    const rows = debts.map(d => `"${d.name}",${d.day_due},${d.balance},${d.promo_balance},"${d.apr_now}","${d.promo_until || ""}","${d.apr_after}",${d.statement_min},${d.current_balance},${d.is_deferred_interest}`);
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "the-vault-debts.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split("\n").filter(l => l.trim());
    if (lines.length < 2) { toast.error("CSV must have a header row and data"); return; }
    let imported = 0;
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].match(/(".*?"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, "").trim()) || [];
      if (cols.length < 4) continue;
      try {
        await axios.post(`${api}/debts`, {
          name: cols[0] || `Debt ${i}`, day_due: parseInt(cols[1]) || 1,
          balance: parseFloat(cols[2]) || 0, promo_balance: parseFloat(cols[3]) || 0,
          apr_now: cols[4] || "0%", promo_until: cols[5] || null, apr_after: cols[6] || "0%",
          statement_min: parseFloat(cols[7]) || 0, current_balance: parseFloat(cols[8]) || 0,
          is_deferred_interest: cols[9]?.toLowerCase() === "true",
        });
        imported++;
      } catch (err) { console.error(`Row ${i} failed:`, err); }
    }
    toast.success(`Imported ${imported} debts`);
    onRefresh();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const totalBalance = debts.reduce((s, d) => s + (d.total_balance ?? 0), 0);
  const totalCurrent = debts.reduce((s, d) => s + (d.current_balance ?? d.total_balance ?? 0), 0);
  const totalMin = debts.reduce((s, d) => s + d.statement_min, 0);

  return (
    <div data-testid="debt-tracker-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Debt Tracker</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Drag rows to reorder</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportCSV} data-testid="import-csv-input" />
          <Button data-testid="bt-simulator-btn" variant="outline" onClick={() => setBtOpen(true)} disabled={debts.length === 0} className="rounded-sm gap-1.5 text-xs">
            <Repeat className="w-3.5 h-3.5" strokeWidth={1.5} /><span className="hidden sm:inline">BT Simulator</span>
          </Button>
          {debts.length > 0 && (
            <Button
              data-testid="sync-from-budget-btn"
              variant="outline"
              onClick={handleSyncFromBudget}
              title="Re-pull every debt's balance from your paid Monthly Budget rows"
              className="rounded-sm gap-1.5 text-xs"
            >
              <Repeat className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Sync from Budget</span>
            </Button>
          )}
          <Button data-testid="import-csv-btn" variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-sm gap-1.5 text-xs">
            <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /><span className="hidden sm:inline">Import CSV</span>
          </Button>
          {debts.length > 0 && (
            <Button data-testid="export-debts-csv-btn" variant="outline" onClick={exportDebtsCSV} className="rounded-sm gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />Export CSV
            </Button>
          )}
          <Button data-testid="add-debt-btn" onClick={handleAdd} className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2 mobile-fab">
            <Plus className="w-4 h-4" strokeWidth={1.5} /><span>Add Debt</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 text-sm border-b border-gray-200 pb-3">
        <div>
          <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold mr-2">Total Balance:</span>
          <span data-testid="debt-total-balance" className="font-mono-data font-semibold text-gray-700">{fmt(totalBalance)}</span>
        </div>
        <div>
          <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold mr-2">Current Outstanding:</span>
          <span data-testid="debt-total-current" className="font-mono-data font-semibold text-red-700">{fmt(totalCurrent)}</span>
        </div>
        <div>
          <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold mr-2">Total Min:</span>
          <span data-testid="debt-total-min" className="font-mono-data font-semibold">{fmt(totalMin)}</span>
        </div>
        <div>
          <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold mr-2">Count:</span>
          <span data-testid="debt-count" className="font-mono-data font-semibold">{debts.length}</span>
        </div>
      </div>

      <div className="border border-gray-200 rounded-sm overflow-x-auto custom-scroll">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="spreadsheet-table w-full">
            <thead>
              <tr>
                <th className="text-center w-8"></th>
                <th className="text-left">Debt Name</th>
                <th className="text-center mobile-hide">Day Due</th>
                <th className="text-right mobile-hide">Balance (Retail)</th>
                <th className="text-right mobile-hide">Promo Bal</th>
                <th className="text-right">Total Balance</th>
                <th className="text-center mobile-hide">Util %</th>
                <th className="text-center">APR</th>
                <th className="text-center mobile-hide">0% Until</th>
                <th className="text-center hidden">APR After</th>
                <th className="text-right">Stmt Min £</th>
                <th className="text-right mobile-hide">Current Bal</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <SortableContext items={debts.map(d => d.id)} strategy={verticalListSortingStrategy}>
              <tbody>
                {debts.length === 0 && (
                  <tr><td colSpan={13} className="text-center text-gray-400 py-8">No debts added yet.</td></tr>
                )}
                {debts.map((d, i) => (
                  <SortableDebtRow key={d.id} d={d} i={i} handleEdit={handleEdit} setDeleteTarget={setDeleteTarget} onShowHistory={setHistoryDebt} onAddPurchase={setPurchaseDebt} onSetCurrentBalance={setSetCBDebt} />
                ))}
                {debts.length > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td></td>
                    <td className="text-xs uppercase tracking-wider text-gray-600">TOTALS</td>
                    <td className="mobile-hide"></td><td className="mobile-hide"></td><td className="mobile-hide"></td>
                    <td className="numeric text-red-700">{fmt(totalBalance)}</td>
                    <td className="mobile-hide"></td>
                    <td></td><td className="mobile-hide"></td><td className="hidden"></td>
                    <td className="numeric">{fmt(totalMin)}</td>
                    <td className="numeric mobile-hide">{fmt(debts.reduce((s, d) => s + d.current_balance, 0))}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </div>

      {dialogOpen && (
        <DebtDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} initialData={editDebt} />
      )}

      <DebtPaymentHistoryDialog
        open={!!historyDebt}
        onClose={() => setHistoryDebt(null)}
        debt={historyDebt}
        api={api}
        onDataChanged={onRefresh}
      />

      <DebtPurchaseDialog
        open={!!purchaseDebt}
        onClose={() => setPurchaseDebt(null)}
        debt={purchaseDebt}
        api={api}
        onSaved={onRefresh}
      />

      <SetCurrentBalanceDialog
        open={!!setCBDebt}
        onClose={() => setSetCBDebt(null)}
        debt={setCBDebt}
        api={api}
        onSaved={onRefresh}
      />

      <BalanceTransferDialog
        open={btOpen}
        onClose={() => setBtOpen(false)}
        debts={debts}
      />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget?.id)}
        title="Delete Debt Account?"
        message={`This will permanently remove "${deleteTarget?.name}" and all its projected payments from the payoff plan. This action cannot be undone.`}
        itemName={deleteTarget?.name}
        requireTypeName={true}
      />
    </div>
  );
}
